// src/pages/RoleSelection.js
// One-time role selection after account creation.
// Route: /welcome (protected — requires auth)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import LogoDark from '../assets/divine-frameworks-logo.svg';
import { trackEvent } from '../utils/analytics';

const RoleSelection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [user, setUser] = useState(null);

  // Check auth + existing role
  useEffect(() => {
    let done = false;
    const timeout = setTimeout(() => {
      if (!done) { done = true; setLoading(false); navigate('/login'); }
    }, 5000);

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (done) return;
      done = true;
      clearTimeout(timeout);

      if (!firebaseUser) {
        navigate('/login');
        return;
      }

      setUser(firebaseUser);

      // Check if user already has a role set
      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          const role = userDoc.data().role;
          if (role === 'practitioner' || role === 'admin') {
            navigate('/dashboard/practitioner');
            return;
          }
          if (role === 'seeker') {
            navigate('/my-sessions');
            return;
          }
        }
      } catch (err) {
        console.error('Role check failed:', err);
      }

      setLoading(false);
    });

    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, [navigate]);

  const handleSelectRole = async (role) => {
    if (!user || selecting) return;
    setSelecting(true);

    try {
      const userRef = doc(db, 'users', user.uid);

      if (role === 'practitioner') {
        // Update role
        await setDoc(userRef, { role: 'practitioner', updatedAt: serverTimestamp() }, { merge: true });

        // Create practitioner document
        const practRef = doc(db, 'practitioners', user.uid);
        const practSnap = await getDoc(practRef);
        if (!practSnap.exists()) {
          await setDoc(practRef, {
            profile: {
              displayName: user.displayName || user.email?.split('@')[0] || '',
              email: user.email,
              photo: user.photoURL || null,
            },
            services: [],
            createdAt: serverTimestamp(),
            verification: {
              identityVerified: false,
              identityVerifiedAt: null,
              manualReviewStatus: 'pending',
              manualReviewedAt: null,
              manualReviewedBy: null,
              rejectionFeedback: null,
              profileComplete: false,
              profileCompleteness: {
                hasName: false,
                hasPhoto: false,
                hasBio: false,
                hasService: false,
                hasAvailability: false,
              },
              backgroundCheckStatus: 'not_started',
              backgroundCheckBadgeAwarded: false,
              excellenceBadge: false,
            },
            qualityStatus: 'healthy',
            qualityStatusUpdatedAt: null,
          });
        }

        localStorage.setItem('userRole', 'practitioner');
        trackEvent('signup_complete', { role: 'practitioner' });
        navigate('/onboarding');
      } else {
        // Seeker — just update role, no practitioner doc
        await setDoc(userRef, { role: 'seeker', updatedAt: serverTimestamp() }, { merge: true });
        localStorage.setItem('userRole', 'seeker');
        trackEvent('signup_complete', { role: 'seeker' });
        navigate('/my-sessions');
      }
    } catch (err) {
      console.error('Role selection failed:', err);
      setSelecting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={styles.spinner} />
        </div>
        <style>{`@keyframes rsSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <img src={LogoDark} alt="Divine Frameworks" style={styles.logo} />
          <span style={styles.logoText}>Divine Frameworks</span>
        </div>

        <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', marginBottom: '8px', textAlign: 'center' }}>
          What brings you to Divine Frameworks?
        </h1>
        <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '32px', textAlign: 'center' }}>
          You can always change this later.
        </p>

        {/* Seeker card */}
        <div
          role="button" tabIndex={0}
          onClick={() => !selecting && handleSelectRole('seeker')}
          onKeyDown={(e) => e.key === 'Enter' && !selecting && handleSelectRole('seeker')}
          style={styles.roleCard}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#bb9749'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
        >
          <div style={{ fontSize: '28px', marginBottom: '12px' }}>🔍</div>
          <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: '0 0 6px' }}>
            I'm looking for a practitioner
          </p>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
            Find and book sessions with trusted spiritual and wellness practitioners
          </p>
        </div>

        {/* Practitioner card */}
        <div
          role="button" tabIndex={0}
          onClick={() => !selecting && handleSelectRole('practitioner')}
          onKeyDown={(e) => e.key === 'Enter' && !selecting && handleSelectRole('practitioner')}
          style={{ ...styles.roleCard, marginTop: '12px' }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#bb9749'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
        >
          <div style={{ fontSize: '28px', marginBottom: '12px' }}>✨</div>
          <p style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', margin: '0 0 6px' }}>
            I'm a practitioner
          </p>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
            Build your practice with tools for booking, payments, and client management
          </p>
        </div>

        {selecting && (
          <p style={{ textAlign: 'center', fontSize: '14px', color: '#6B7280', marginTop: '16px' }}>
            Setting up your account...
          </p>
        )}
      </div>
      <style>{`@keyframes rsSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh', background: '#F9FAFB',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    padding: '40px 20px',
    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  container: {
    width: '100%', maxWidth: '480px',
  },
  logoContainer: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '12px', marginBottom: '40px',
  },
  logo: { height: '85px', width: 'auto' },
  logoText: {
    fontSize: '20px', fontWeight: 500, color: '#000',
    fontFamily: "'Montserrat', sans-serif",
    textTransform: 'uppercase', letterSpacing: '0.025em',
  },
  roleCard: {
    width: '100%',
    padding: '24px',
    background: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'Montserrat', sans-serif",
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  },
  spinner: {
    width: '32px', height: '32px', border: '3px solid #E5E7EB', borderTopColor: '#bb9749',
    borderRadius: '50%', animation: 'rsSpin 0.8s linear infinite', margin: '0 auto',
  },
};

export default RoleSelection;
